"use client";

import { Calendar, User, ArrowLeft, Share2, Tag } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-12 w-3/4 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-1/2 bg-muted/30 animate-pulse rounded-md" />
        <div className="aspect-[16/9] w-full bg-muted/30 animate-pulse rounded-lg" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 w-full bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 w-5/6 bg-muted/30 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-muted-foreground">Unable to load this article. Please try again later.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-muted-foreground">Article not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "AI Guyz Editorial";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"];
  const postDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="max-w-4xl mx-auto">
      {}
      <div className="mb-8">
        <Link
          to="#"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to home</span>
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <Link
              key={cat.id}
              data-index={i}
              to={cat.link || "#"}
              className="inline-flex items-center px-3 py-1 text-xs font-medium tracking-wider uppercase border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight mb-6 text-foreground"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground mb-10">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-primary" />
          <span>{authorName}</span>
        </div>
        {postDate && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <span>{postDate}</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto text-muted-foreground hover:text-primary"
        >
          <Share2 className="h-4 w-4" />
          <span className="ml-2">Share</span>
        </Button>
      </div>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg border border-border">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {}
      <article
        className="prose prose-lg max-w-none font-default text-foreground/90 leading-relaxed prose-headings:font-default prose-headings:text-foreground prose-headings:font-semibold prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:text-foreground/80 prose-blockquote:italic prose-img:rounded-lg prose-img:border prose-img:border-border prose-code:text-primary prose-code:bg-muted/40 prose-code:px-1 prose-code:py-0.5 prose-code:rounded [&>*]:mb-6"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="h-4 w-4 text-primary mr-1" />
            {post.tags.map((tag, i) => (
              <Link
                key={tag.id}
                data-index={i}
                to={tag.link || "#"}
                className="inline-flex items-center px-3 py-1 text-xs border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
              >
                #{tag.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {}
      <Card className="bg-card border border-border shadow-md">
        <CardContent className="p-8">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <Avatar className="h-20 w-20 rounded-lg border border-border">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-muted text-foreground font-default text-xl">
                {authorName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-primary mb-2">
                Written by
              </p>
              <h3 className="font-default text-2xl font-semibold text-foreground mb-2">
                {authorName}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                AI Guyz is a veteran-owned, father-and-son agency in Vacaville, CA. We specialize in 24/7 AI automation, custom web design, and branding to help local businesses scale effortlessly.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-4">
            <div className="aspect-[4/3] bg-muted/30 animate-pulse rounded-lg" />
            <div className="h-5 w-3/4 bg-muted/30 animate-pulse rounded-md" />
            <div className="h-4 w-1/2 bg-muted/20 animate-pulse rounded-md" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-muted-foreground text-center">No related articles available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.slice(0, 3).map((post, i) => {
        const dateStr = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <Link
            key={post.id}
            data-index={i}
            to={post.link || "#"}
            className="group block"
          >
            <article className="space-y-4">
              <div className="aspect-[4/3] overflow-hidden rounded-lg border border-border bg-muted/20">
                {post.featuredImage ? (
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <span className="font-default text-2xl">AI Guyz</span>
                  </div>
                )}
              </div>
              {post.categories && post.categories.length > 0 && (
                <p className="text-xs uppercase tracking-widest text-primary">
                  {post.categories[0].name}
                </p>
              )}
              <h3
                className="font-default text-xl font-semibold text-foreground leading-snug group-hover:text-primary transition-colors"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />
              {dateStr && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span>{dateStr}</span>
                </div>
              )}
            </article>
          </Link>
        );
      })}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const singlePostQuery = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: currentWPQueryParams?.post_type || "post",
    per_page: 3,
  });

  return (
    <section data-section-id="5123"
      id="single-post-template-section"
      className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={singlePostQuery}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="max-w-6xl mx-auto mt-24 md:mt-32">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-primary mb-3">
                Continue Reading
              </p>
              <h2 className="font-default text-3xl md:text-4xl font-semibold text-foreground">
                Related Articles
              </h2>
            </div>
            <div className="h-px flex-1 bg-border ml-8 hidden md:block" />
          </div>
          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}